import { InferGetServerSidePropsType, NextPage } from 'next';
import React, { FC } from 'react';
import MetaTags, { COUBIC_ORIGIN } from '../../components/layouts/MetaTags';
import { MerchantSite } from '../../components/MerchantSite';
import MerchantShowPage from '../../templates/merchants/DetailTemplate/MerchantShowPage';
import ErrorTemplate from '../../templates/ErrorTemplate';
import {
  allCategoriesCache,
  getCategory,
} from '../../infrastructure/api/backend/explore_api';
import { CategoryEntity } from '../../interfaces/explore/CategoryEntity';
import { MerchantDetail } from '../../interfaces/merchants';
import { MerchantReview } from '../../interfaces/merchants/reviews';
import {
  fetchMerchantDetail,
  fetchMerchantReviewsForSSR,
} from '../../infrastructure/api';
import {
  AppGetServerSidePropsContext,
  injectAppContextGetServerSideProps,
} from '../_app';
import { usePushMerchantDataLayerEffect } from '../../hooks/gtm';

interface Context extends AppGetServerSidePropsContext {
  query: {
    publicId: string;
  };
}

interface Props {
  reviews?: MerchantReview[];
  merchantDetail?: MerchantDetail;
  categories: CategoryEntity[];
  category?: CategoryEntity;
  isUnpublishedSite: boolean;
  merchantPublicId: string;
  isCoubicApp: boolean;
}

const MerchantComponent: FC<Props> = ({
  merchantDetail,
  category,
  categories,
  reviews,
  isCoubicApp,
}) => {
  if (merchantDetail && category && reviews) {
    return (
      <>
        <MetaTags
          title={
            merchantDetail.seo_setting?.title || merchantDetail.display_name
          }
          description={
            merchantDetail.seo_setting?.description ||
            merchantDetail.description
          }
          keywords={merchantDetail.seo_setting?.keyword || ''}
          thumbnailUrl={merchantDetail.cover_image.url || ''}
          currentUrl={`${COUBIC_ORIGIN}/${merchantDetail.public_id}`}
          noIndex={merchantDetail.seo_setting?.no_index}
          googleAnalyticsFourTrackingId={
            merchantDetail.available_google_analytics_four_tracking_id
          }
          faviconImageUrl={merchantDetail?.favicon_image?.url}
        />
        <MerchantShowPage
          merchantDetail={merchantDetail}
          category={category}
          categories={categories}
          reviews={reviews}
          isCoubicApp={isCoubicApp}
        />
      </>
    );
  }
  return <ErrorTemplate statusCode={404} />;
};

const MerchantShow: NextPage<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = (props) => {
  const { isUnpublishedSite, merchantDetail } = props;
  usePushMerchantDataLayerEffect({ visit_merchant_id: merchantDetail?.id });

  return (
    <MerchantSite
      isUnPublished={isUnpublishedSite}
      isMerchantInfoExist={!!merchantDetail}
      logoImage={merchantDetail?.logo_image || null}
      merchantPublicId={merchantDetail?.public_id || null}
      isCoubicApp={props.isCoubicApp}
    >
      <MerchantComponent {...props} />
    </MerchantSite>
  );
};

export const getServerSideProps = injectAppContextGetServerSideProps<Props>(
  async (ctx: Context) => {
    const {
      session,
      token,
      requestId,
      query: { publicId: merchantPublicId },
      isCoubicApp,
    } = ctx;

    const [merchantDetailResponse, categories, reviews] = await Promise.all([
      // Layoutで取得している部分を徐々に置き換え中
      fetchMerchantDetail({ session, token, requestId, merchantPublicId }),
      allCategoriesCache(),
      // 最新10件だけ取得
      fetchMerchantReviewsForSSR({
        session,
        token,
        requestId,
        merchantPublicId,
        page: 1,
        perPage: 10,
      }),
    ]).catch((err) => {
      throw err;
    });
    const { merchant, isUnpublishedSite } = merchantDetailResponse;
    const category = merchant
      ? await getCategory(merchant.category_id)
      : undefined;
    return {
      props: {
        categories,
        category,
        merchantDetail: merchant,
        reviews: reviews?.data,
        isUnpublishedSite,
        merchantPublicId,
        isCoubicApp,
      },
    };
  },
);

export default MerchantShow;
